import { useState } from "react"
import { useMsal } from "@azure/msal-react"
import { Navigate, useLocation } from "react-router-dom"
import { resolveLoginRedirectStartPage } from "@/v2-console-shared/auth/auth.redirect"
import Button from "@/v2-backoffice-client/components/button"
import { login } from "@/v2-backoffice-client/auth/auth.instance"

// Backoffice's home — where a sign-in lands when the guard didn't request a
// specific ?r= return path.
const HOME_PAGE = "/"

function AuthLoginPage() {
  const { accounts } = useMsal()
  const location = useLocation()
  const [isLoggingIn, setIsLoggingIn] = useState(false)

  // Where to land after sign-in: AuthGuard's ?r= deep link when present
  // (validated as an in-app path), the home page otherwise (FUS-900).
  const returnTo =
    resolveLoginRedirectStartPage(location, HOME_PAGE) ?? HOME_PAGE

  // If already logged in, redirect to the requested page
  if (accounts.length > 0) {
    return <Navigate to={returnTo} replace />
  }

  return (
    <div className="flex items-center justify-center flex-1 bg-background">
      <div className="max-w-md w-full space-y-8 p-10 bg-card rounded-lg shadow-xl border">
        <div className="text-center">
          <div className="mx-auto h-16 w-16 bg-primary rounded-full flex items-center justify-center mb-4">
            <svg
              className="h-10 w-10 text-primary-foreground"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
              />
            </svg>
          </div>
          <h2 className="text-3xl font-bold text-foreground mb-2">
            Placepoint Backoffice
          </h2>
          <p className="text-muted-foreground mb-8">
            Administration interface for managing users and teams
          </p>
        </div>

        <div className="space-y-4">
          <Button
            size="lg"
            className="w-full"
            isLoading={isLoggingIn}
            onClick={() => {
              setIsLoggingIn(true)
              // Without redirectStartPage MSAL returns to the URL that
              // started the login — this /login page — leaving the user on
              // the login card after a successful sign-in (FUS-900).
              login({ redirectStartPage: returnTo })
            }}
          >
            {isLoggingIn ? "Redirecting to login..." : "Sign in with Microsoft"}
          </Button>

          <div className="text-center text-sm text-muted-foreground">
            <p>Use your organizational account to sign in</p>
          </div>
        </div>

        <div className="border-t pt-6 mt-6">
          <div className="text-xs text-muted-foreground space-y-2">
            <p className="flex items-center justify-center gap-1">
              <svg className="h-4 w-4" fill="currentColor" viewBox="0 0 20 20">
                <path
                  fillRule="evenodd"
                  clipRule="evenodd"
                  d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z"
                />
              </svg>
              Secured with Azure Active Directory
            </p>
            <p className="text-center">
              Access restricted to authorized personnel
            </p>
          </div>
        </div>
      </div>
    </div>
  )
}

export default AuthLoginPage
